/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {usePrimeCache} from "../../../../Hooks/ServiceHooks";
import {service as ContactService} from "../../../../Services/ContactService";
import {service as ProjectStakeholderRelationshipService} from "../../../../Services/ProjectStakeholderRelationshipService";
import {service as RelationshipService} from "../../../../Services/RelationshipService";
import {ContactDetails} from "../../../Pages/Projects/ContactDetails";
import {ConfirmationDialog} from "../../ConfirmationDialog";
import {StreamBankLink} from "../../StreamBankLink";
import {ContactFieldEditor} from "../../../../Models/Core/ServiceDrivenEditors";
import {useDebouncedIsValidNewOption} from "../../../../Hooks/useDebouncedIsValidNewOption";
import {useBuildQueryString, useGetUrlContextSegment, usePatchContext} from "../../../../Hooks/RouteHooks";
import {ObjectUtils} from "../../../../Utils";
import {useProjectContacts, useReloadProjectContacts} from "../ProjectContactsContext";
import {buildAnswer} from "../../../../Hooks/SurveyFormHooks";

/**
 * Survey input that displays project contacts for a given relationship type.
 *
 * In edit mode, if multiple contacts match, a radio button allows selecting
 * which one to record. The selected contact's stakeholderId and displayName
 * are stored in valueNumber/valueText as a point-in-time snapshot.
 *
 * Below the list (always, in edit mode) a contact search lets the user assign
 * an existing org contact to this relationship for this project. If the typed
 * name doesn't match any existing contact, a "+ Add new contact" option opens
 * /contacts/new in a new tab. Picking an existing contact opens a confirmation
 * modal before posting the new ProjectStakeholderRelationship.
 *
 * Configure via SurveyQuestion.options JSON:
 *   {"relationship": "Landowner"}
 *
 * The relationship name must match an existing Relationship.name value exactly.
 *
 * Project contacts are loaded once by ProjectContactsProvider in SurveyForm and
 * shared via context — not fetched per-question.
 */
export const SurveyFormInputContactRelationship = (props) => {
    const {
        ConservationAction: conservationAction,
        Question: question,
        Answer: answer,
        OnChange: onChange = () => {},
        EditMode,
    } = props;

    usePrimeCache([RelationshipService]);

    const patchContext = usePatchContext();
    const projectContext = patchContext({ conservationAction: null });
    const getUrlContextSegment = useGetUrlContextSegment();
    const buildQueryString = useBuildQueryString();

    // The assignable-contact search is scoped to the PROJECT's organization (from the
    // navigation context), not the current user's. Otherwise a user acting across orgs
    // (e.g. an admin whose org differs from the project's) could attach a contact from
    // their own org to this project, which users in the project's org then can't see.
    const { organizationId: projectOrganizationId } = useSelector(state => state.contextState.context ?? {});

    const projectContacts = useProjectContacts();
    const reloadProjectContacts = useReloadProjectContacts();
    const [selectedStakeholderId, setSelectedStakeholderId] = useState(null);
    const [resolvedContact, setResolvedContact] = useState(null);
    const [pendingContact, setPendingContact] = useState(null);
    const [searchKey, setSearchKey] = useState(0); // bump to clear the search field
    const [searchInput, setSearchInput] = useState('');

    const rawOptions = question?.options ?? {};
    const options = ObjectUtils.IsString(rawOptions) ? JSON.parse(rawOptions) : rawOptions;
    const relationshipName = options?.relationship ?? null;

    const targetRelationshipId = RelationshipService.CachedValueByProperty(relationshipName, 'name')?.result?.relationshipId ?? null;

    const matchingRelationships = useMemo(() => {
        if (!projectContacts || targetRelationshipId === null) return null;
        return projectContacts.flatMap(contact => {
            const hasContactInfo = ContactService.HasRequiredContactInfo(contact);
            return (contact.ProjectStakeholderRelationship ?? [])
                .filter(rel => rel.relationshipId === targetRelationshipId)
                .map(rel => ({
                    key: rel.projectStakeholderRelationshipId,
                    Stakeholder: contact,
                    stakeholderId: contact.stakeholderId,
                    displayName: contact.displayName,
                    hasContactInfo,
                }));
        });
    }, [projectContacts, targetRelationshipId]);

    const writeAnswer = (stakeholderId, displayName, hasContactInfo = false) => {
        onChange(buildAnswer(question, conservationAction, answer, {
            valueNumeric: stakeholderId ?? null,
            // Trimmed: Stakeholder.displayName is a stored generated column that keeps
            // the name's surrounding whitespace, while the server trims every string
            // on write. The snapshot must match what comes back after a save, or the
            // page reads as changed on every load and no save can clear it.
            valueText: displayName?.trim() ?? null,
            // Send int, not JS bool — server stores tinyint(1) and PDO returns int;
            // sending `true` here makes the strict-comparison change detector flag
            // every save as a false positive. Null only when no contact is selected.
            valueBool: stakeholderId == null ? null : (hasContactInfo ? 1 : 0),
        }));
    };

    // Initialize selection once contacts are available. `answer` and `writeAnswer` are
    // intentionally excluded from deps: we read answer.valueNumber once at load time to
    // restore a stored selection, and never want to re-initialize when onChange updates
    // the answer — that would loop since matchingRelationships is stable after load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!matchingRelationships) return;

        // Legacy free-text answer (from before this question became contact_relationship):
        // valueNumeric is null but valueText is set and doesn't match any current contact.
        // Don't auto-populate a stakeholderId over it — let the user explicitly select one.
        const isLegacyText = !answer?.valueNumeric && answer?.valueText &&
            !matchingRelationships.some(r => r.displayName === answer.valueText);
        if (isLegacyText) return;

        const storedMatch = answer?.valueNumeric != null
            ? matchingRelationships.find(r => r.stakeholderId === answer.valueNumeric)
            : null;

        // A stored selection (valueNumeric) that isn't in the currently-visible set is
        // preserved as-is rather than re-derived: an absent match can mean the contact
        // belongs to another organization this viewer can't see, or that contacts
        // haven't loaded — not that the relationship was removed. Overwriting here would
        // silently null or re-point a valid recorded contact, and a save would persist
        // the loss.
        if (answer?.valueNumeric != null && !storedMatch) {
            setSelectedStakeholderId(answer.valueNumeric);
            return;
        }

        // Prefer a stored answer, then any complete contact, then fall back to
        // the first match (which may be incomplete — the user can still pick it
        // and the missing-fields warning will surface for that selection).
        const initial = storedMatch
            ?? matchingRelationships.find(r => r.hasContactInfo)
            ?? matchingRelationships[0]
            ?? null;

        setSelectedStakeholderId(initial?.stakeholderId ?? null);
        // When no replacement contact is available, preserve the previously
        // saved valueText so the empty block renders it as a "Previously
        // recorded as: …" tombstone. The pre-existing legacy-text branch in
        // isComplete keeps the section reading complete in that state, which
        // matches what a true pre-migration legacy answer looks like — a
        // previously-valid answer that survives until the user actively
        // edits the survey to pick a replacement.
        const preservedValueText = initial?.displayName ?? answer?.valueText ?? null;
        writeAnswer(initial?.stakeholderId ?? null, preservedValueText, initial?.hasContactInfo ?? false);
    }, [matchingRelationships]);

    // When the stored selection isn't in the visible set, try resolving the
    // contact: a viewer who can load it (e.g. an admin) gets told when it isn't
    // listed because its organization differs from the project's, plus a fresher
    // name than the answer's denormalized valueText. A viewer who can't load it
    // just gets the plain "Previously recorded as" line off valueText.
    const storedStakeholderId = answer?.valueNumeric ?? null;
    useEffect(() => {
        setResolvedContact(null);
        if (storedStakeholderId == null || !matchingRelationships) return;
        if (matchingRelationships.some(r => r.stakeholderId === storedStakeholderId)) return;
        let cancelled = false;
        ContactService.FindById(storedStakeholderId)
            .then(response => {
                if (!cancelled && response?.result) setResolvedContact(response.result);
            })
            .catch(() => {}); // not loadable by this viewer — fall back to valueText
        return () => { cancelled = true; };
    }, [storedStakeholderId, matchingRelationships]);

    const handleSelectionChange = (rel) => {
        setSelectedStakeholderId(rel.stakeholderId);
        writeAnswer(rel.stakeholderId, rel.displayName, rel.hasContactInfo);
    };

    // ── search / assign-existing flow ───────────────────────────────────────
    // isValidNewOption: surface a "+ Add new contact" option whenever the user
    // has typed something. react-select hides it automatically if an exact-label
    // match exists in the search results, so this is safe to leave permissive.
    const validateNewOption = async (inputValue) => !!(inputValue && inputValue.trim());
    const [isValidNewOption, isValidatingNew] = useDebouncedIsValidNewOption(validateNewOption, searchInput, 250);

    const openNewContactTab = (inputValue) => {
        const params = {relationship: relationshipName, fromSurvey: 1};
        if (inputValue) {
            // Split at the first space: firstName = first word,
            // lastName = everything else. User can adjust before saving.
            const trimmed = inputValue.trim();
            const firstSpace = trimmed.indexOf(' ');
            if (firstSpace === -1) {
                params.firstName = trimmed;
            } else {
                params.firstName = trimmed.substring(0, firstSpace);
                params.lastName = trimmed.substring(firstSpace + 1).trim();
            }
        }
        const href = getUrlContextSegment(projectContext) + "/contacts/new" + buildQueryString(params);
        window.open(href, '_blank');
    };

    const handleContactPicked = (contact) => {
        if (!contact?.stakeholderId) return;
        setPendingContact(contact);
    };

    // While the assign modal is open, prefer the fresh copy from
    // ProjectContactsProvider (which reloads on visibilitychange after the Edit
    // Contact tab returns) over the original search-result snapshot. Falls back
    // to the snapshot for contacts not yet associated with this project.
    const livePendingContact = useMemo(() => {
        if (!pendingContact) return null;
        const fresh = projectContacts?.find(c => c.stakeholderId === pendingContact.stakeholderId);
        return fresh ?? pendingContact;
    }, [pendingContact, projectContacts]);

    const closeModal = () => {
        setPendingContact(null);
        // Remount the editor to clear its internal selected state.
        setSearchKey(k => k + 1);
        setSearchInput('');
    };

    const editPendingContact = () => {
        if (!pendingContact) return;
        const params = {fromSurvey: 1, edit: 1, relationship: relationshipName};
        const href = getUrlContextSegment(projectContext) +
            "/contacts/" + pendingContact.stakeholderId + buildQueryString(params);
        window.open(href, '_blank');
        // Leave the modal open. When the user returns to this tab, the
        // visibility-change handler in ProjectContactsProvider reloads project
        // contacts; if the user saved with the relationship added, the
        // idempotent POST below will be a no-op.
    };

    const assignPendingContact = async () => {
        if (!pendingContact) return;
        const contact = pendingContact;
        try {
            await ProjectStakeholderRelationshipService.Create(
                contact.stakeholderId,
                conservationAction.projectId,
                targetRelationshipId
            );
        } catch (e) {
            console.error("Failed to assign relationship", e);
            return;
        }
        // Persist the new selection in the answer before reload, so the
        // init effect re-finds it on the refreshed matchingRelationships list.
        const hasContactInfo = ContactService.HasRequiredContactInfo(contact);
        const displayName = contact.displayName ?? ContactService.GetTitle(contact);
        setSelectedStakeholderId(contact.stakeholderId);
        writeAnswer(contact.stakeholderId, displayName, hasContactInfo);
        await reloadProjectContacts();
        closeModal();
    };

    if (matchingRelationships === null) {
        return null;
    }

    // When no contact card renders for this relationship, still surface any
    // recorded name instead of implying nothing is recorded: a legacy free-text
    // value (valueText with no stakeholderId), or a stored selection whose
    // contact isn't in the visible set. Only a viewer who could resolve the
    // contact gets told the reason it isn't listed (cross-org mismatch) —
    // everyone else just sees the plain previously-recorded line.
    const resolvedContactName = resolvedContact
        ? (resolvedContact.displayName ?? ContactService.GetTitle(resolvedContact))
        : null;
    const recordedName = resolvedContactName ?? answer?.valueText ?? null;
    const recordedContactCrossOrg = resolvedContact != null && projectOrganizationId != null
        && Number(resolvedContact.organizationId) !== Number(projectOrganizationId);

    // Always render the radio in edit mode so the user can see which contact is
    // currently selected as the answer, even when only one match exists.
    const showRadios = EditMode;

    return (
        <div className={`sb-survey-contact-relationship${!EditMode ? ' read-only' : ''}`}>
            {matchingRelationships.length === 0 && (
                <div className="sb-survey-contact-relationship-empty">
                    {recordedName ?
                        <p className="sb-survey-contact-relationship-legacy">
                            {recordedContactCrossOrg
                                ? <>Recorded contact not visible to this project's organization: <em>{recordedName}</em></>
                                : <>Previously recorded as: <em>{recordedName}</em></>}
                        </p> :
                        <p>No <em>{relationshipName}</em> contact found for this project.</p>
                    }
                </div>
            )}

            {matchingRelationships.map(rel => {
                const ItemTag = showRadios ? 'label' : 'div';
                const radioId = `contact-rel-${rel.key}`;
                const isSelected = selectedStakeholderId === rel.stakeholderId;
                return <ItemTag
                    key={rel.key}
                    className={`sb-survey-contact-relationship-item${isSelected ? ' selected' : ''}`}
                    htmlFor={showRadios ? radioId : undefined}
                >
                    {showRadios && (
                        <input
                            id={radioId}
                            type="radio"
                            className="sb-survey-contact-relationship-radio"
                            checked={isSelected}
                            onChange={() => handleSelectionChange(rel)}
                        />
                    )}
                    <div className="sb-survey-contact-relationship-item-details">
                        <ContactDetails
                            key={rel.key}
                            contact={rel.Stakeholder}
                            stakeholderId={rel.stakeholderId}
                            linkName={false}
                            showMissingFields={!showRadios || isSelected}
                        />
                    </div>
                    {isSelected && (() => {
                        const needsUpdate = EditMode && !rel.hasContactInfo;
                        const params = {fromSurvey: 1, relationship: relationshipName};
                        if (EditMode) params.edit = 1;
                        return (
                            <div className="sb-survey-contact-relationship-actions">
                                <StreamBankLink
                                    Context={projectContext}
                                    Tool={`contacts/${rel.stakeholderId}`}
                                    ToolParam={params}
                                    target="_blank"
                                    className={needsUpdate
                                        ? "btn btn-primary"
                                        : "btn btn-link sb-survey-contact-relationship-details-link"}
                                >
                                    {needsUpdate ? "Update Contact..." : "Contact Details..."}
                                </StreamBankLink>
                            </div>
                        );
                    })()}
                </ItemTag>;
            })}

            {EditMode && (
                <div className="sb-survey-contact-relationship-search">
                    <label className="sb-survey-contact-relationship-search-label">
                        {matchingRelationships.length > 0
                            ? "Or assign a different contact, or add a new one:"
                            : "Assign an existing contact, or add a new one:"}
                    </label>
                    <ContactFieldEditor
                        key={searchKey}
                        Value={null}
                        placeholder="Contact name…"
                        additionalSearchParams={{organizationId: projectOrganizationId}}
                        OnUpdate={() => {}}
                        OnUpdateModel={handleContactPicked}
                        onInputChange={setSearchInput}
                        isLoading={isValidatingNew}
                        isValidNewOption={isValidNewOption}
                        getNewOptionData={(inputValue, label) => ({
                            // `label` is the string returned by formatCreateLabel.
                            // StreamBankAutoCompleteItem reads `option.label` for the menu
                            // text — without it, the renderer falls back to
                            // ContactService.GetTitle({}) which returns "(No name)".
                            label,
                            // displayName mirrors ContactService.TitleProperty so the
                            // autocomplete's filterOption (which calls GetSearchableString)
                            // matches the typed text and keeps this synthetic visible.
                            displayName: inputValue,
                            _isCreateNew: true,
                            stakeholderId: null,
                            _typedName: inputValue,
                        })}
                        formatCreateLabel={(inputValue) =>
                            inputValue
                                ? `+ Add "${inputValue}" as a new contact`
                                : `+ Add a new contact`
                        }
                        onCreateOption={(inputValue) => {
                            openNewContactTab(inputValue);
                            setSearchKey(k => k + 1);
                            setSearchInput('');
                        }}
                    />
                </div>
            )}

            {pendingContact && (
                <ConfirmationDialog
                    Display={true}
                    Title={`Assign ${relationshipName} relationship?`}
                    OnHide={closeModal}
                    OnClose={closeModal}
                    CancelButtonMessage="Cancel"
                    OnCustom={editPendingContact}
                    CustomButtonMessage="Edit Contact"
                    OnConfirm={assignPendingContact}
                    ConfirmationButtonMessage={`Assign ${relationshipName}`}
                >
                    <p>
                        Add <strong>{relationshipName}</strong> relationship for this project to
                        the following contact?
                    </p>
                    {/* Reuse the survey item layout so the contact card picks up the same
                        border, padding, and typography rules used on the radio list above. */}
                    <div className="sb-survey-contact-relationship">
                        <div className="sb-survey-contact-relationship-item">
                            <div className="sb-survey-contact-relationship-item-details">
                                <ContactDetails
                                    key={livePendingContact?.modified ?? pendingContact.stakeholderId}
                                    contact={livePendingContact}
                                    stakeholderId={pendingContact.stakeholderId}
                                    linkName={false}
                                    showMissingFields={true}
                                />
                            </div>
                        </div>
                    </div>
                </ConfirmationDialog>
            )}
        </div>
    );
};
